/**
 * Firebase Configuration and Crashlytics Setup
 * 
 * To use Firebase Crashlytics:
 * 1. Go to https://console.firebase.google.com/
 * 2. Create a new project (or use existing)
 * 3. Add a Web app and Android app
 * 4. Copy the config below and replace the placeholder values
 * 5. For Android: Download google-services.json and place in android/app/
 */

import { initializeApp } from 'firebase/app';
import { getAnalytics, logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { Capacitor } from '@capacitor/core';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyABUO1ipVwYm3gsqNZYVpZd3WJPG6VrEwQ",
  authDomain: "igo-global.firebaseapp.com",
  projectId: "igo-global",
  storageBucket: "igo-global.firebasestorage.app",
  messagingSenderId: "991288500373",
  appId: "1:991288500373:web:fde26e6209accacf35fb9c",
  measurementId: "G-BPZNWHET0B"
};

// Firebase is now configured with user's project
const isFirebaseConfigured = true;

let app = null;
let analytics = null;

// Initialize Firebase only if configured
if (isFirebaseConfigured) {
  try {
    app = initializeApp(firebaseConfig);
    
    // Only initialize analytics in browser (not in SSR or native without proper setup)
    if (typeof window !== 'undefined' && !Capacitor.isNativePlatform()) {
      analytics = getAnalytics(app);
      console.log('[Firebase] Initialized successfully');
    }
  } catch (error) {
    console.warn('[Firebase] Initialization failed:', error.message);
  }
} else {
  console.log('[Firebase] Not configured - crash reporting disabled');
}

/**
 * Error Tracking Service
 * Captures and reports errors to Firebase/Console
 */
class ErrorTracker {
  constructor() {
    this.isEnabled = isFirebaseConfigured;
    this.userId = null;
    this.setupGlobalErrorHandlers();
  }

  /**
   * Set up global error handlers
   */
  setupGlobalErrorHandlers() {
    // Handle uncaught errors
    window.onerror = (message, source, lineno, colno, error) => {
      this.captureError(error || new Error(message), {
        source,
        lineno,
        colno,
        type: 'uncaught'
      });
      return false; // Let the error propagate
    };

    // Handle unhandled promise rejections
    window.onunhandledrejection = (event) => {
      this.captureError(event.reason, {
        type: 'unhandled_promise_rejection'
      });
    };
  }

  /**
   * Set the current user ID for error tracking
   */
  setUser(userId, userEmail = null) {
    this.userId = userId;
    
    if (analytics && userId) {
      try {
        setUserId(analytics, userId);
        if (userEmail) {
          setUserProperties(analytics, {
            user_email_domain: userEmail.split('@')[1] || 'unknown'
          });
        }
      } catch (error) {
        console.warn('[ErrorTracker] Failed to set user:', error);
      }
    }
  }

  /**
   * Clear user data (on logout)
   */
  clearUser() {
    this.userId = null;
    if (analytics) {
      try {
        setUserId(analytics, null);
      } catch (error) {
        // Ignore
      }
    }
  }

  /**
   * Capture and report an error
   */
  captureError(error, context = {}) {
    const errorInfo = {
      message: error?.message || String(error),
      stack: error?.stack || 'No stack trace',
      name: error?.name || 'Error',
      timestamp: new Date().toISOString(),
      userId: this.userId,
      platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web',
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...context
    };

    // Always log to console
    console.error('[ErrorTracker] Captured error:', errorInfo);

    // Send to Firebase Analytics as an event
    if (analytics) {
      try {
        logEvent(analytics, 'app_error', {
          error_message: errorInfo.message.substring(0, 100),
          error_name: errorInfo.name,
          error_type: context.type || 'caught',
          platform: errorInfo.platform,
          page: window.location.pathname
        });
      } catch (e) {
        console.warn('[ErrorTracker] Failed to log to Firebase:', e);
      }
    }

    return errorInfo;
  }

  /**
   * Log a custom event
   */
  logEvent(eventName, params = {}) {
    if (analytics) {
      try {
        logEvent(analytics, eventName, {
          ...params,
          platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web',
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.warn('[ErrorTracker] Failed to log event:', error);
      }
    }
  }

  /**
   * Log screen/page view
   */
  logScreenView(screenName, screenClass = 'App') {
    this.logEvent('screen_view', {
      screen_name: screenName,
      screen_class: screenClass
    });
  }

  /**
   * Log user action
   */
  logAction(action, category, label = null, value = null) {
    this.logEvent('user_action', {
      action,
      category,
      label,
      value
    });
  }
}

// Create singleton instance
const errorTracker = new ErrorTracker();

// Export for use in app
export { app, analytics, errorTracker };
export default errorTracker;
